import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { OPEN_ATTEMPTS_QUERY_KEY, type OpenAttemptRow } from './continue-api';
import { startPractice } from './practice-api';
import type { StartOverDialogProps, StartOverTarget } from './StartOverDialog';

const START_ERROR: Record<string, string> = {
  student_archived:
    'Your account has been archived by your institute, so new attempts are disabled. Contact your institute if this is unexpected.',
  test_unavailable: 'This test is no longer available.',
};
const START_ERROR_FALLBACK = 'Could not start that test. Please try again.';
/** Said inside the dialog, where the student is looking at the old attempt:
 *  the delete is all-or-nothing, so a failure means it is still there. */
const RESTART_ERROR_FALLBACK =
  'Couldn’t start over — your unfinished attempt is still there. Please try again.';
/** The server answered but handed the OLD attempt back: a start-practice build
 *  from before Phase 159 has no `restart` and resumes instead. Navigating
 *  there would look like success and drop the student straight back into the
 *  paper they asked to throw away — the very bug Phase 196 fixes. */
const RESTART_UNAVAILABLE =
  'Starting over isn’t available right now — your unfinished attempt is still there. You can continue it, or try again later.';

function startErrorMessage(code: string | undefined, fallback: string): string {
  return START_ERROR[code ?? ''] ?? fallback;
}

export interface StartPracticeFlow {
  /**
   * Start this test — or, if the student already has it open, ask whether to
   * continue that attempt or start over. The server decides which: it resumes
   * an open practice attempt rather than creating a second one.
   */
  start: (testId: string, testTitle: string) => void;
  /** Open the same Continue / Start over choice for an attempt the page
   *  already knows is open, without asking the server first. */
  offerStartOver: (target: Omit<StartOverTarget, 'row'>) => void;
  /** The test whose Start is in flight, for a per-row spinner. */
  pendingTestId: string | null;
  error: string | null;
  /** Spread onto `<StartOverDialog />`. */
  dialogProps: StartOverDialogProps;
}

/**
 * The one way a student starts a practice test.
 *
 * Every Start / Retake / Try again in the student area goes through here, so
 * an unfinished attempt always gets the same choice — Continue it, or Start
 * over, which deletes it (answers, highlights, drafts, recordings) and opens a
 * fresh one. Before Phase 196 only the Mock Tests page offered Start over, and
 * the dashboard, Results and drill lists silently dropped the student back
 * into the old attempt.
 */
export function useStartPractice(): StartPracticeFlow {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<StartOverTarget | null>(null);
  const [restartError, setRestartError] = useState<string | null>(null);

  // Progress for the dialog comes from the open-attempts list only if a page
  // already loaded it — never fetched here, so a failing list can never stand
  // between the student and the choice.
  const cachedRow = useCallback(
    (attemptId: string): OpenAttemptRow | null =>
      queryClient
        .getQueryData<OpenAttemptRow[]>(OPEN_ATTEMPTS_QUERY_KEY)
        ?.find((row) => row.attempt_id === attemptId) ?? null,
    [queryClient],
  );

  const openChoice = useCallback(
    (next: Omit<StartOverTarget, 'row'>) => {
      setRestartError(null);
      setTarget({ ...next, row: cachedRow(next.attemptId) });
    },
    [cachedRow],
  );

  const {
    mutate: startMutate,
    isPending: startPending,
    variables: startVariables,
  } = useMutation({
    mutationFn: (vars: { testId: string; testTitle: string }) => startPractice(vars.testId),
    onMutate: () => setError(null),
    onSuccess: (res, vars) => {
      if (!res.ok || res.attempt === undefined) {
        setError(startErrorMessage(res.error, START_ERROR_FALLBACK));
        return;
      }
      if (res.resumed === true) {
        openChoice({ testId: vars.testId, testTitle: vars.testTitle, attemptId: res.attempt.id });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: OPEN_ATTEMPTS_QUERY_KEY });
      navigate(`/student/practice/${res.attempt.id}`);
    },
    onError: () => setError(START_ERROR_FALLBACK),
  });

  const { mutate: restartMutate, isPending: restartPending } = useMutation({
    mutationFn: (vars: { testId: string; attemptId: string }) =>
      startPractice(vars.testId, { restart: true }),
    onMutate: () => setRestartError(null),
    onSuccess: (res, vars) => {
      if (!res.ok || res.attempt === undefined) {
        setRestartError(startErrorMessage(res.error, RESTART_ERROR_FALLBACK));
        return;
      }
      if (res.resumed === true || res.attempt.id === vars.attemptId) {
        setRestartError(RESTART_UNAVAILABLE);
        return;
      }
      setTarget(null);
      void queryClient.invalidateQueries({ queryKey: OPEN_ATTEMPTS_QUERY_KEY });
      navigate(`/student/practice/${res.attempt.id}`);
    },
    onError: () => setRestartError(RESTART_ERROR_FALLBACK),
  });

  const start = useCallback(
    (testId: string, testTitle: string) => startMutate({ testId, testTitle }),
    [startMutate],
  );

  return {
    start,
    offerStartOver: openChoice,
    pendingTestId: startPending ? (startVariables?.testId ?? null) : null,
    error,
    dialogProps: {
      target,
      restarting: restartPending,
      error: restartError,
      onContinue: () => {
        if (target === null) return;
        setTarget(null);
        navigate(`/student/practice/${target.attemptId}`);
      },
      onStartOver: () => {
        if (target !== null) {
          restartMutate({ testId: target.testId, attemptId: target.attemptId });
        }
      },
      // Closing mid-delete would hide the outcome of an irreversible action.
      onClose: () => {
        if (!restartPending) setTarget(null);
      },
    },
  };
}
